/**
 * Базовый класс типизированного элемента
 */

import { CSElement } from '../core/CSElement';
import { EventEmitter } from 'events';
import {
  ITypedElement,
  TypedElementSchema,
  TypedFieldSchema,
  TypedValidationResult,
  FieldValidationResult,
  SerializedTypedElement,
  FieldType,
  TypedElementEventType,
  TypedElementEvent
} from '../types/typed-elements-interfaces';
import { ElementOptions } from '../types/interfaces';

/**
 * Базовый класс для типизированных элементов
 */
export class TypedElement<T = any> extends CSElement implements ITypedElement<T> {
  private _typeName: string;
  private _schema: TypedElementSchema<T>;
  private _typedData: Partial<T> = {};
  private _validationCache = new Map<string, FieldValidationResult>();
  private _lastValidation: TypedValidationResult | null = null;
  private _eventEmitter = new EventEmitter();
  private _manager: any = null; // Ссылка на менеджер для получения наследованных полей

  constructor(
    value: any,
    schema: TypedElementSchema<T>,
    options?: ElementOptions,
    manager?: any
  ) {
    super(value, options);
    this._typeName = schema.name;
    this._schema = schema;
    this._manager = manager;
    this.initializeFields();
  }

  // ===== Геттеры =====

  get typeName(): string {
    return this._typeName;
  }

  get schema(): TypedElementSchema<T> {
    return this._schema;
  }

  get isAbstract(): boolean {
    return this._schema.abstract || false;
  }

  get isSealed(): boolean {
    return this._schema.sealed || false;
  }

  get inheritanceChain(): string[] {
    if (this._manager && this._manager.buildInheritanceChain) {
      return this._manager.buildInheritanceChain(this._typeName);
    }
    
    // Fallback для случая без менеджера
    const chain: string[] = [this._typeName];
    const current = this._schema.inheritFrom;
    
    while (current) {
      chain.push(current);
      // Без менеджера не можем получить схему родителя
      break;
    }
    
    return chain;
  }

  // ===== Инициализация =====

  private initializeFields(): void {
    // Получаем все поля с учетом наследования
    const allFields = this.getAllFields();
    
    // Инициализируем поля значениями по умолчанию
    for (const field of allFields) {
      if (field.defaultValue !== undefined) {
        this._typedData[field.name] = field.defaultValue;
      }
    }
  }

  /**
   * Получает все поля с учетом наследования
   */
  private getAllFields(): TypedFieldSchema<T>[] {
    if (this._manager && this._manager.getInheritedFields) {
      return this._manager.getInheritedFields(this._typeName);
    }
    
    // Fallback для случая без менеджера
    return this._schema.fields || [];
  }

  // ===== Типизированные методы для работы с данными =====

  getTypedData(): Partial<T> {
    return { ...this._typedData };
  }

  async setTypedData(data: Partial<T>): Promise<void> {
    const oldData = { ...this._typedData };
    
    // Валидация всех полей
    for (const [fieldName, value] of Object.entries(data)) {
      const validationResult = await this.validateField(fieldName as keyof T, value as T[keyof T]);
      if (!validationResult.isValid) {
        throw new Error(`Validation failed for field ${fieldName}: ${validationResult.errors.join(', ')}`);
      }
    }

    // Обновляем данные
    this._typedData = { ...this._typedData, ...data };
    
    // Синхронизируем с базовым CSElement
    for (const [key, value] of Object.entries(data)) {
      await this.setData(key, value);
    }

    // Эмитим событие
    this.emitTypedEvent({
      type: TypedElementEventType.TypedDataChanged,
      element: this,
      oldValue: oldData,
      newValue: this._typedData,
      timestamp: Date.now()
    });
  }

  getField<K extends keyof T>(fieldName: K): T[K] | undefined {
    return this._typedData[fieldName];
  }

  async setField<K extends keyof T>(fieldName: K, value: T[K]): Promise<void> {
    const oldValue = this._typedData[fieldName];
    
    // Валидация поля
    const validationResult = await this.validateField(fieldName, value);
    if (!validationResult.isValid) {
      throw new Error(`Validation failed for field ${String(fieldName)}: ${validationResult.errors.join(', ')}`);
    }

    // Обновляем данные
    this._typedData[fieldName] = value;
    
    // Синхронизируем с базовым CSElement
    await this.setData(String(fieldName), value);

    // Эмитим событие
    this.emitTypedEvent({
      type: TypedElementEventType.FieldChanged,
      element: this,
      fieldName,
      oldValue,
      newValue: value,
      timestamp: Date.now()
    });
  }

  // ===== Валидация =====

  async validateType(): Promise<TypedValidationResult> {
    const fieldErrors = new Map<string, FieldValidationResult>();
    const schemaErrors: string[] = [];
    const inheritanceErrors: string[] = [];
    let isValid = true;

    // Получаем все поля с учетом наследования
    const allFields = this.getAllFields();

    // Валидация всех полей
    for (const field of allFields) {
      const value = this._typedData[field.name];
      const fieldResult = await this.validateField(field.name, value as T[keyof T]);
      
      if (!fieldResult.isValid) {
        isValid = false;
        fieldErrors.set(String(field.name), fieldResult);
      }
    }

    // Валидация схемы
    if (this.isAbstract && this._typedData) {
      schemaErrors.push('Cannot instantiate abstract type');
      isValid = false;
    }

    // Валидация наследования
    if (this._schema.inheritFrom) {
      // Проверяем, что родительский тип существует
      if (this._manager && this._manager.getSchema) {
        const parentSchema = this._manager.getSchema(this._schema.inheritFrom);
        if (!parentSchema) {
          inheritanceErrors.push(`Parent type ${this._schema.inheritFrom} not found`);
          isValid = false;
        }
      }
    }

    // Валидация интерфейсов
    const implementedInterfaces = this._schema.metadata?.implementedInterfaces || [];
    for (const interfaceName of implementedInterfaces) {
      if (this._manager && this._manager.getSchema) {
        const interfaceSchema = this._manager.getSchema(interfaceName);
        if (!interfaceSchema) {
          inheritanceErrors.push(`Interface ${interfaceName} not found`);
          isValid = false;
        } else if (!interfaceSchema.abstract) {
          inheritanceErrors.push(`${interfaceName} is not an interface`);
          isValid = false;
        }
      }
    }

    const result: TypedValidationResult = {
      isValid,
      errors: [...schemaErrors, ...inheritanceErrors].map(err => ({ 
        field: 'schema', 
        message: err 
      })),
      fieldErrors,
      schemaErrors,
      inheritanceErrors
    };

    // Кэшируем результат
    this._lastValidation = result;

    // Эмитим событие
    this.emitTypedEvent({
      type: isValid ? TypedElementEventType.ValidationPassed : TypedElementEventType.ValidationFailed,
      element: this,
      validationResult: result,
      timestamp: Date.now()
    });

    return result;
  }

  async validateField<K extends keyof T>(fieldName: K, value: T[K]): Promise<FieldValidationResult> {
    const fieldNameStr = String(fieldName);
    
    // Получаем все поля с учетом наследования
    const allFields = this.getAllFields();
    const fieldSchema = allFields.find(f => String(f.name) === fieldNameStr);
    
    if (!fieldSchema) {
      return {
        isValid: false,
        errors: [`Field ${fieldNameStr} not found in schema`],
        warnings: [],
        fieldName: fieldNameStr,
        value,
        expectedType: 'unknown' as FieldType,
        actualType: this.getActualType(value)
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];

    // Проверка обязательности поля
    if (fieldSchema.required && (value === undefined || value === null)) {
      errors.push(`Field ${fieldNameStr} is required`);
    }

    // Проверка типа
    if (value !== undefined && value !== null) {
      if (!this.isTypeCompatible(value, fieldSchema.type)) {
        errors.push(`Field ${fieldNameStr} expected type ${fieldSchema.type}, got ${this.getActualType(value)}`);
      }

      // Дополнительная валидация
      if (fieldSchema.validation) {
        const validation = fieldSchema.validation;
        
        // Валидация диапазона
        if (typeof value === 'number') {
          if (validation.min !== undefined && value < validation.min) {
            errors.push(`Field ${fieldNameStr} must be >= ${validation.min}`);
          }
          if (validation.max !== undefined && value > validation.max) {
            errors.push(`Field ${fieldNameStr} must be <= ${validation.max}`);
          }
        }

        // Валидация длины строки
        if (typeof value === 'string') {
          if (validation.min !== undefined && value.length < validation.min) {
            errors.push(`Field ${fieldNameStr} must be at least ${validation.min} characters`);
          }
          if (validation.max !== undefined && value.length > validation.max) {
            errors.push(`Field ${fieldNameStr} must be at most ${validation.max} characters`);
          }
        }

        // Валидация паттерна
        if (validation.pattern && typeof value === 'string') {
          const regex = new RegExp(validation.pattern);
          if (!regex.test(value)) {
            errors.push(`Field ${fieldNameStr} does not match pattern ${validation.pattern}`);
          }
        }

        // Валидация enum
        if (validation.enum && !validation.enum.includes(value)) {
          errors.push(`Field ${fieldNameStr} must be one of: ${validation.enum.join(', ')}`);
        }

        // Кастомная валидация
        if (validation.custom) {
          const customResult = validation.custom(value);
          if (customResult !== true) {
            errors.push(typeof customResult === 'string' ? customResult : `Field ${fieldNameStr} failed custom validation`);
          }
        }
      }
    }

    const result: FieldValidationResult = {
      isValid: errors.length === 0,
      errors,
      warnings,
      fieldName: fieldNameStr,
      value,
      expectedType: fieldSchema.type,
      actualType: this.getActualType(value)
    };

    // Кэшируем результат
    this._validationCache.set(fieldNameStr, result);

    return result;
  }

  private getActualType(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (Array.isArray(value)) return 'array';
    if (value instanceof Date) return 'date';
    return typeof value;
  }

  private isTypeCompatible(value: any, expectedType: FieldType): boolean {
    const actualType = this.getActualType(value);
    
    switch (expectedType) {
      case 'string':
        return actualType === 'string';
      case 'number':
        return actualType === 'number';
      case 'boolean':
        return actualType === 'boolean';
      case 'date':
        return actualType === 'date';
      case 'array':
        return actualType === 'array';
      case 'object':
        return actualType === 'object';
      case 'reference':
        return value instanceof CSElement;
      case 'computed':
        return true; // Computed fields can be any type
      case 'enum':
        return true; // Enum validation is handled separately
      case 'union':
        return true; // Union validation is handled separately
      case 'generic':
        return true; // Generic validation is handled separately
      default:
        return false;
    }
  }

  // ===== Наследование =====

  canInheritFrom(typeName: string): boolean {
    if (this._manager && this._manager.canInherit) {
      return this._manager.canInherit(this._typeName, typeName);
    }
    
    // Базовая проверка без менеджера
    return this._schema.inheritFrom === typeName;
  }

  getInheritedFields(): TypedFieldSchema<T>[] {
    return this.getAllFields();
  }

  /**
   * Проверяет, является ли элемент экземпляром указанного типа или его наследником
   */
  isInstanceOf(typeName: string): boolean {
    return this.inheritanceChain.includes(typeName);
  }

  /**
   * Проверяет, реализует ли элемент указанный интерфейс
   */
  implementsInterface(interfaceName: string): boolean {
    const implementedInterfaces = this._schema.metadata?.implementedInterfaces || [];
    return implementedInterfaces.includes(interfaceName);
  }

  /**
   * Получает метаданные о наследовании
   */
  getInheritanceMetadata(): {
    parentType: string | null;
    implementedInterfaces: string[];
    inheritanceDepth: number;
    isComposite: boolean;
    sourceSchemas?: string[];
  } {
    const metadata = this._schema.metadata || {};
    
    return {
      parentType: this._schema.inheritFrom || null,
      implementedInterfaces: metadata.implementedInterfaces || [],
      inheritanceDepth: metadata.inheritanceDepth || 0,
      isComposite: metadata.isComposite || false,
      sourceSchemas: metadata.sourceSchemas
    };
  }

  // ===== Сериализация с типами =====

  serializeTyped(): SerializedTypedElement<T> {
    return {
      id: this.id,
      name: this.name,
      typeName: this._typeName,
      schema: this._schema,
      typedData: this.getTypedData(),
      rawData: this.data,
      metadata: {
        createdAt: Date.now(),
        updatedAt: Date.now(),
        version: this._schema.version,
        inheritanceChain: this.inheritanceChain
      }
    };
  }

  // ===== Клонирование с типами =====

  async cloneTyped(): Promise<ITypedElement<T>> {
    const cloned = new TypedElement<T>(
      this.name,
      this._schema,
      {
        name: `${this.name}_clone`,
        data: new Map(this.data)
      },
      this._manager
    );

    await cloned.setTypedData(this.getTypedData());
    return cloned;
  }

  // ===== События =====

  private emitTypedEvent(event: TypedElementEvent<T>): void {
    this._eventEmitter.emit(event.type, event);
  }

  onTypedEvent(eventType: TypedElementEventType, listener: (event: TypedElementEvent<T>) => void): void {
    this._eventEmitter.on(eventType, listener);
  }

  offTypedEvent(eventType: TypedElementEventType, listener: (event: TypedElementEvent<T>) => void): void {
    this._eventEmitter.off(eventType, listener);
  }

  // ===== Переопределение методов CSElement =====

  async setData(key: string, value: any): Promise<void> {
    // Синхронизируем с типизированными данными
    const fieldSchema = this._schema.fields.find(f => String(f.name) === key);
    if (fieldSchema) {
      this._typedData[fieldSchema.name] = value;
    }
    
    await super.setData(key, value);
  }

  // ===== Утилиты =====

  getValidationCache(): Map<string, FieldValidationResult> {
    return new Map(this._validationCache);
  }

  getLastValidation(): TypedValidationResult | null {
    return this._lastValidation;
  }

  clearValidationCache(): void {
    this._validationCache.clear();
    this._lastValidation = null;
  }

  // ===== Статические методы =====

  static async createTyped<T>(
    name: string,
    schema: TypedElementSchema<T>,
    data?: Partial<T>
  ): Promise<TypedElement<T>> {
    const element = new TypedElement<T>(name, schema);
    
    if (data) {
      await element.setTypedData(data);
    }
    
    return element;
  }
} 